import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Trophy, Crown, Medal, Lock } from "lucide-react";

const rankColor = (r) => (r === 1 ? "text-amber-400" : r === 2 ? "text-slate-300" : r === 3 ? "text-orange-400" : "text-slate-500");

export const Leaderboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard").then((res) => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17] grain relative">
      <Header />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="relative max-w-3xl mx-auto px-5 py-12">
        <div className="text-center fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 mb-4">
            <Trophy className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Anonymous rankings</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Global Leaderboard</h1>
          <p className="mt-3 text-slate-400">Premium test-takers who opted in, ranked by IQ. All entries are anonymous.</p>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 font-mono-data text-sm py-16">Loading rankings…</div>
        ) : !data || data.total === 0 ? (
          <div className="text-center py-16 rounded-3xl bg-[#131B2A] border border-white/10 mt-10">
            <Lock className="w-8 h-8 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-300">No one has joined the leaderboard yet.</p>
            <p className="text-slate-500 text-sm mt-1">Unlock a Premium report and opt in to claim the top spot.</p>
            <button onClick={() => navigate("/history")} className="mt-4 text-cyan-400 hover:underline">Go to my results →</button>
          </div>
        ) : (
          <>
            {data.you && (
              <div data-testid="your-rank-card" className="mt-9 flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-cyan-500/15 to-indigo-500/15 border border-cyan-500/40 fade-up">
                <div className="font-sub font-black text-3xl text-cyan-300 w-14 text-center">#{data.you.rank}</div>
                <div className="flex-1">
                  <div className="font-semibold text-white">Your rank</div>
                  <div className="text-sm text-slate-400">IQ {data.you.iq} · {data.you.band}</div>
                </div>
                <div className="text-sm text-slate-400">of {data.total}</div>
              </div>
            )}

            <div className="mt-6 rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/5">
              {data.top.map((e) => (
                <div
                  key={e.rank}
                  data-testid={`leaderboard-row-${e.rank}`}
                  className={`flex items-center gap-4 p-4 ${e.is_you ? "bg-cyan-500/10" : "bg-[#131B2A]"}`}
                >
                  <div className={`font-sub font-black text-2xl w-10 text-center ${rankColor(e.rank)}`}>
                    {e.rank === 1 ? <Crown className="w-6 h-6 mx-auto" /> : e.rank <= 3 ? <Medal className="w-5 h-5 mx-auto" /> : e.rank}
                  </div>
                  <div className="flex-1">
                    <div className={`font-semibold ${e.is_you ? "text-cyan-300" : "text-slate-200"}`}>{e.label}</div>
                    <div className="text-xs text-slate-500">{e.band}</div>
                  </div>
                  <div className="font-mono-data font-bold text-lg text-white">IQ {e.iq}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
